import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

export function DropdownNavigation({ navItems }) {
  const [openMenu, setOpenMenu] = useState(null);
  const [isHover, setIsHover] = useState(null);
  const navigate = useNavigate();

  const handleHover = (menuLabel) => setOpenMenu(menuLabel);

  const handleItemNav = (link) => {
    setOpenMenu(null);
    if (!link) return;
    if (link.startsWith('/#')) {
      if (window.location.pathname !== '/') {
        navigate('/');
        setTimeout(() => {
          const el = document.getElementById(link.slice(2));
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 100);
      } else {
        const el = document.getElementById(link.slice(2));
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }
    } else {
      navigate(link);
    }
  };

  return (
    <ul className="relative flex items-center space-x-0">
      {navItems.map((navItem) => (
        <li
          key={navItem.label}
          className="relative"
          onMouseEnter={() => handleHover(navItem.label)}
          onMouseLeave={() => handleHover(null)}
        >
          <button
            data-testid={`nav-item-${navItem.label.toLowerCase().replace(/\s+/g, '-')}`}
            className="text-sm py-1.5 px-4 flex cursor-pointer group transition-colors duration-300 items-center justify-center gap-1 relative"
            style={{ color: (isHover === navItem.id || openMenu === navItem.label) ? 'var(--c-text)' : 'var(--c-muted)' }}
            onMouseEnter={() => setIsHover(navItem.id)}
            onMouseLeave={() => setIsHover(null)}
            onClick={() => navItem.link && handleItemNav(navItem.link)}
          >
            <span>{navItem.label}</span>
            {navItem.subMenus && (
              <ChevronDown
                className={`h-4 w-4 group-hover:rotate-180 duration-300 transition-transform ${openMenu === navItem.label ? "rotate-180" : ""}`}
              />
            )}
            {(isHover === navItem.id || openMenu === navItem.label) && (
              <motion.div
                layoutId="hover-bg"
                className="absolute inset-0 size-full bg-[#D97A46]/10"
                style={{ borderRadius: 99 }}
              />
            )}
          </button>

          <AnimatePresence>
            {openMenu === navItem.label && navItem.subMenus && (
              <div className="w-auto absolute left-0 top-full pt-2 z-50">
                <motion.div
                  className="border border-[#273A2F] p-4 w-max shadow-2xl"
                  style={{ borderRadius: 16, backgroundColor: '#16201A' }}
                  layoutId="menu"
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -8 }}
                  transition={{ duration: 0.2 }}
                >
                  <div className="w-fit shrink-0 flex space-x-9 overflow-hidden">
                    {navItem.subMenus.map((sub) => (
                      <motion.div layout className="w-full" key={sub.title}>
                        <h3 className="mb-4 text-xs font-semibold uppercase tracking-wider text-[#A8BCAE]">
                          {sub.title}
                        </h3>
                        <ul className="space-y-3">
                          {sub.items.map((item) => {
                            const Icon = item.icon;
                            return (
                              <li key={item.label}>
                                <button
                                  data-testid={`nav-subitem-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                                  onClick={() => item.link && handleItemNav(item.link)}
                                  className="flex items-start space-x-3 group w-full text-left"
                                >
                                  <div className="border border-[#273A2F] text-[#A8BCAE] rounded-md flex items-center justify-center size-9 shrink-0 group-hover:bg-[#D97A46]/10 group-hover:text-[#D97A46] group-hover:border-[#D97A46]/30 transition-colors duration-200">
                                    <Icon className="h-4 w-4 flex-none" />
                                  </div>
                                  <div className="leading-5 w-max">
                                    <p className="text-sm font-medium text-[#F2F4F3] shrink-0">{item.label}</p>
                                    <p className="text-xs text-[#A8BCAE] shrink-0 group-hover:text-[#F2F4F3] transition-colors duration-200">{item.description}</p>
                                  </div>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </motion.div>
                    ))}
                  </div>
                </motion.div>
              </div>
            )}
          </AnimatePresence>
        </li>
      ))}
    </ul>
  );
}
